import { useState, useEffect } from "react";
import { useNavigate } from "react-router-dom";
import axios from "axios";
import { toast } from "sonner";
import { format } from "date-fns";
import {
  ArrowLeft,
  CalendarIcon,
  Clock,
  Loader2,
  MapPin,
  User,
  Mail,
  Phone,
  PartyPopper,
} from "lucide-react";
import { useCart } from "@/lib/CartContext";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Separator } from "@/components/ui/separator";
import { Calendar } from "@/components/ui/calendar";
import {
  Popover,
  PopoverContent,
  PopoverTrigger,
} from "@/components/ui/popover";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const EVENT_TYPES = [
  "Birthday Party",
  "Wedding",
  "Anniversary",
  "Corporate Event",
  "Family Get-together",
  "Religious Ceremony",
  "Graduation Party",
  "Baby Shower",
  "Holiday Celebration",
  "Other",
];

export default function CheckoutPage() {
  const navigate = useNavigate();
  const { cart, subtotal, clearCart } = useCart();
  const [loading, setLoading] = useState(false);
  const [eventDate, setEventDate] = useState(null);

  useEffect(() => {
    window.scrollTo(0, 0);
  }, []);

  const [form, setForm] = useState({
    name: "",
    email: "",
    phone: "",
    event_type: "",
    delivery_address: "",
    delivery_time: "",
  });

  const handleChange = (field, value) => {
    setForm((prev) => ({ ...prev, [field]: value }));
  };

  const isValid =
    form.name &&
    form.email &&
    form.phone &&
    form.event_type &&
    eventDate &&
    form.delivery_address &&
    form.delivery_time &&
    cart.length > 0;

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!isValid) {
      toast.error("Please fill all required fields");
      return;
    }
    setLoading(true);
    try {
      const payload = {
        items: cart.map((item) => ({
          menu_item_id: item.menuItemId,
          name: item.name,
          size: item.size,
          quantity: item.quantity,
          price: item.price,
        })),
        customer: {
          ...form,
          event_date: format(eventDate, "MMMM d, yyyy"),
        },
      };
      const res = await axios.post(`${API}/orders`, payload);
      clearCart();
      navigate("/confirmation", {
        state: {
          orderId: res.data.id,
          subtotal: res.data.subtotal,
          customerName: form.name,
        },
      });
      toast.success("Order placed successfully!");
    } catch (err) {
      console.error(err);
      toast.error("Failed to place order. Please try again.");
    } finally {
      setLoading(false);
    }
  };

  if (cart.length === 0) {
    return (
      <div className="min-h-[60vh] flex flex-col items-center justify-center px-4">
        <div className="text-center animate-fade-up">
          <div className="w-20 h-20 rounded-full bg-stone-100 flex items-center justify-center mx-auto mb-6">
            <span className="text-3xl font-heading text-stone-300">~</span>
          </div>
          <h2 className="font-heading text-2xl text-[#78350F] mb-2">
            Your cart is empty
          </h2>
          <p className="font-body text-stone-500 text-sm mb-6">
            Add some items from the menu first
          </p>
          <Button
            data-testid="go-to-menu-btn"
            onClick={() => navigate("/menu")}
            className="rounded-full bg-[#EA580C] hover:bg-[#C2410C] text-white font-body font-semibold px-8"
          >
            Browse Menu
          </Button>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen">
      <div className="bg-[#78350F] py-8 sm:py-10">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <button
            data-testid="back-to-menu"
            onClick={() => navigate("/menu")}
            className="flex items-center gap-2 text-[#FED7AA] hover:text-white font-body text-sm mb-4 transition-colors"
          >
            <ArrowLeft className="h-4 w-4" /> Back to Menu
          </button>
          <h1 className="font-heading text-3xl sm:text-4xl font-bold text-white animate-fade-up">
            Checkout
          </h1>
        </div>
      </div>

      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-8 sm:py-12">
        <form
          onSubmit={handleSubmit}
          className="grid grid-cols-1 lg:grid-cols-5 gap-8 lg:gap-12"
        >
          {/* Form Section */}
          <div className="lg:col-span-3 space-y-6" data-testid="checkout-form">
            <div className="bg-white rounded-2xl border border-stone-100 p-6 sm:p-8 shadow-[0_2px_12px_rgb(0,0,0,0.03)]">
              <h2 className="font-heading text-xl text-[#78350F] mb-6">
                Your Details
              </h2>
              <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                <div className="space-y-2">
                  <Label className="font-body text-sm text-stone-600 flex items-center gap-2">
                    <User className="h-3.5 w-3.5" /> Full Name
                  </Label>
                  <Input
                    data-testid="input-name"
                    placeholder="Your full name"
                    value={form.name}
                    onChange={(e) => handleChange("name", e.target.value)}
                    className="bg-white border-stone-200 focus:border-[#EA580C] focus:ring-[#EA580C]/20 rounded-xl h-11 font-body"
                    required
                  />
                </div>
                <div className="space-y-2">
                  <Label className="font-body text-sm text-stone-600 flex items-center gap-2">
                    <Mail className="h-3.5 w-3.5" /> Email
                  </Label>
                  <Input
                    data-testid="input-email"
                    type="email"
                    placeholder="your@email.com"
                    value={form.email}
                    onChange={(e) => handleChange("email", e.target.value)}
                    className="bg-white border-stone-200 focus:border-[#EA580C] focus:ring-[#EA580C]/20 rounded-xl h-11 font-body"
                    required
                  />
                </div>
                <div className="space-y-2">
                  <Label className="font-body text-sm text-stone-600 flex items-center gap-2">
                    <Phone className="h-3.5 w-3.5" /> Phone
                  </Label>
                  <Input
                    data-testid="input-phone"
                    type="tel"
                    placeholder="(609) 000-0000"
                    value={form.phone}
                    onChange={(e) => handleChange("phone", e.target.value)}
                    className="bg-white border-stone-200 focus:border-[#EA580C] focus:ring-[#EA580C]/20 rounded-xl h-11 font-body"
                    required
                  />
                </div>
                <div className="space-y-2">
                  <Label className="font-body text-sm text-stone-600 flex items-center gap-2">
                    <PartyPopper className="h-3.5 w-3.5" /> Event Type
                  </Label>
                  <Select
                    value={form.event_type}
                    onValueChange={(val) => handleChange("event_type", val)}
                  >
                    <SelectTrigger
                      data-testid="select-event-type"
                      className="bg-white border-stone-200 focus:border-[#EA580C] focus:ring-[#EA580C]/20 rounded-xl h-11 font-body"
                    >
                      <SelectValue placeholder="Select event type" />
                    </SelectTrigger>
                    <SelectContent>
                      {EVENT_TYPES.map((type) => (
                        <SelectItem
                          key={type}
                          value={type}
                          className="font-body"
                        >
                          {type}
                        </SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                </div>
                <div className="space-y-2">
                  <Label className="font-body text-sm text-stone-600 flex items-center gap-2">
                    <CalendarIcon className="h-3.5 w-3.5" /> Event Date
                  </Label>
                  <Popover>
                    <PopoverTrigger asChild>
                      <Button
                        data-testid="select-event-date"
                        variant="outline"
                        className={`w-full justify-start text-left font-body rounded-xl h-11 border-stone-200 hover:bg-stone-50 ${
                          !eventDate && "text-stone-400"
                        }`}
                      >
                        <CalendarIcon className="mr-2 h-4 w-4" />
                        {eventDate
                          ? format(eventDate, "MMMM d, yyyy")
                          : "Pick a date"}
                      </Button>
                    </PopoverTrigger>
                    <PopoverContent className="w-auto p-0" align="start">
                      <Calendar
                        mode="single"
                        selected={eventDate}
                        onSelect={setEventDate}
                        disabled={(date) => date < new Date()}
                        data-testid="calendar-picker"
                      />
                    </PopoverContent>
                  </Popover>
                </div>
                <div className="space-y-2">
                  <Label className="font-body text-sm text-stone-600 flex items-center gap-2">
                    <Clock className="h-3.5 w-3.5" /> Delivery Time
                  </Label>
                  <Input
                    data-testid="input-delivery-time"
                    type="time"
                    value={form.delivery_time}
                    onChange={(e) =>
                      handleChange("delivery_time", e.target.value)
                    }
                    className="bg-white border-stone-200 focus:border-[#EA580C] focus:ring-[#EA580C]/20 rounded-xl h-11 font-body"
                    required
                  />
                </div>
              </div>
              <div className="mt-4 space-y-2">
                <Label className="font-body text-sm text-stone-600 flex items-center gap-2">
                  <MapPin className="h-3.5 w-3.5" /> Delivery Address
                </Label>
                <Input
                  data-testid="input-address"
                  placeholder="Full delivery address"
                  value={form.delivery_address}
                  onChange={(e) =>
                    handleChange("delivery_address", e.target.value)
                  }
                  className="bg-white border-stone-200 focus:border-[#EA580C] focus:ring-[#EA580C]/20 rounded-xl h-11 font-body"
                  required
                />
              </div>
            </div>
          </div>

          {/* Order Summary */}
          <div className="lg:col-span-2" data-testid="order-summary">
            <div className="bg-white rounded-2xl border border-stone-100 p-6 shadow-[0_2px_12px_rgb(0,0,0,0.03)] sticky top-24">
              <h2 className="font-heading text-xl text-[#78350F] mb-4">
                Order Summary
              </h2>
              <div className="space-y-3 max-h-64 overflow-y-auto">
                {cart.map((item) => (
                  <div
                    key={item.cartId}
                    className="flex justify-between items-center text-sm"
                  >
                    <div className="flex-1 min-w-0">
                      <span className="font-body font-medium text-stone-700 truncate block">
                        {item.name}
                      </span>
                      <span className="font-body text-xs text-stone-400">
                        {item.pricingType === "per_piece" || item.size === "per_piece"
                          ? "Per Piece"
                          : item.size === "med_large"
                            ? "Med-Large"
                            : item.size.charAt(0).toUpperCase() + item.size.slice(1)}{" "}
                        x {item.quantity}
                      </span>
                    </div>
                    <span className="font-body font-semibold text-[#78350F] ml-2">
                      ${(item.price * item.quantity).toFixed(2)}
                    </span>
                  </div>
                ))}
              </div>
              <Separator className="my-4" />
              <div className="flex justify-between items-center">
                <span className="font-body text-stone-500">Subtotal</span>
                <span
                  data-testid="checkout-subtotal"
                  className="font-heading text-2xl font-bold text-[#78350F]"
                >
                  ${subtotal.toFixed(2)}
                </span>
              </div>
              <p className="font-body text-xs text-stone-400 mt-2">
                Delivery charges may apply. Final pricing confirmed upon order
                review.
              </p>
              <Button
                type="submit"
                data-testid="place-order-btn"
                disabled={!isValid || loading}
                className="w-full mt-6 rounded-full bg-[#EA580C] hover:bg-[#C2410C] text-white font-body font-semibold h-12 text-base shadow-lg hover:shadow-xl transition-all active:scale-95 disabled:opacity-50"
              >
                {loading ? (
                  <>
                    <Loader2 className="mr-2 h-4 w-4 animate-spin" />
                    Placing Order...
                  </>
                ) : (
                  "Place Order"
                )}
              </Button>
            </div>
          </div>
        </form>
      </div>
    </div>
  );
}
